import React, { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, ExternalLink, Mail, Globe, ArrowUpRight, Sparkles } from "lucide-react";
import Footer from "../../Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";
import { getNewsArticles } from "../../data/newsData";
export default function NewsSection({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t, language } = useLanguage();
  const newsArticles = getNewsArticles(language);
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 50]);

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  /* newsArticles imported from ../data/newsData */

  // Color schemes for variety
  const accentColors = [
    { from: "[--accent]", to: "yellow-400", bg: "[--accent]/10" },
    { from: "purple-500", to: "pink-500", bg: "purple-500/10" },
    { from: "blue-500", to: "cyan-400", bg: "blue-500/10" },
    { from: "emerald-500", to: "teal-400", bg: "emerald-500/10" },
    { from: "orange-500", to: "amber-400", bg: "orange-500/10" },
    { from: "rose-500", to: "pink-400", bg: "rose-500/10" },
    { from: "indigo-500", to: "purple-400", bg: "indigo-500/10" },
    { from: "violet-500", to: "fuchsia-400", bg: "violet-500/10" },
    { from: "sky-500", to: "blue-400", bg: "sky-500/10" }
  ];

  const [featuredArticle] = newsArticles;
  const otherArticles = newsArticles.slice(1);

  return (
    <>
      <section className="min-h-screen py-24 md:py-32 bg-[--bg-secondary] relative overflow-hidden">
        {/* Enhanced Background decoration with parallax */}
        <motion.div style={{ y: y1 }} className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[--accent] rounded-full opacity-10 blur-3xl" />
          <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full opacity-10 blur-3xl" />
        </motion.div>
        <motion.div style={{ y: y2 }} className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl" />
          <div className="absolute bottom-40 left-20 w-80 h-80 bg-pink-500 rounded-full opacity-10 blur-3xl" />
        </motion.div>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          {/* Header with enhanced styling */}
          <motion.div variants={itemAnimation} className="text-center mb-20">
            <motion.div className="inline-flex items-center justify-center mb-6">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
              <motion.span
                className="text-[--accent] text-sm font-medium tracking-[0.3em] uppercase mx-6"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                05
              </motion.span>
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
            </motion.div>

            <h2 className="text-5xl md:text-7xl font-display font-bold text-[--text-primary] mb-4 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-[--text-primary] via-[--accent] to-[--text-primary]">
              {t.news.title}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[--accent] to-transparent mx-auto mt-6" />
          </motion.div>

          {/* Featured Article - Large Horizontal Card */}
          <motion.article
            variants={itemAnimation}
            className="group mb-16"
          >
            <div className="relative glass rounded-3xl overflow-hidden shadow-2xl hover:shadow-luxury-hover transition-all duration-700 border-2 border-transparent hover:border-[--accent]/40">
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/5 via-transparent to-[--accent]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="grid md:grid-cols-2 gap-0">
                {/* Featured Image */}
                <div className="relative h-96 md:h-auto overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-transparent z-10" />
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/20 to-[--accent]/40 flex items-center justify-center hidden">
                    <Globe className="w-16 h-16 text-[--accent]" />
                  </div>

                  {/* Featured Badge */}
                  <div className="absolute top-6 left-6 bg-[--accent] text-white px-6 py-3 rounded-2xl font-bold shadow-lg z-20">
                    <span className="text-sm tracking-wider">{t.news.featured}</span>
                  </div>

                  {/* Date Badge */}
                  <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-md text-white px-5 py-2 rounded-xl text-sm font-semibold flex items-center z-20">
                    <Calendar className="w-4 h-4 mr-2" />
                    {featuredArticle.date}
                  </div>
                </div>

                {/* Featured Content */}
                <div className="p-10 md:p-12 relative z-10 flex flex-col justify-center">
                  <motion.h3
                    className="text-3xl md:text-4xl font-display font-bold text-[--text-primary] mb-6 leading-tight group-hover:text-[--accent] transition-colors duration-300"
                    whileHover={{ x: 10 }}
                  >
                    {featuredArticle.title}
                  </motion.h3>

                  <div className="w-20 h-1 bg-[--accent] mb-6" />

                  <p className="text-[--text-secondary] text-base leading-relaxed mb-5 text-justify">
                    {featuredArticle.description}
                  </p>

                  {featuredArticle.additionalInfo && (
                    <div className="bg-gradient-to-r from-[--accent]/5 to-transparent rounded-2xl p-5 mb-6 border-l-4 border-[--accent]">
                      <p className="text-[--text-secondary] text-sm leading-relaxed italic">
                        {featuredArticle.additionalInfo}
                      </p>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="bg-[--bg-primary]/50 rounded-2xl p-5 mb-6 group-hover:bg-[--accent]/10 transition-colors duration-300">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 rounded-full bg-[--accent]/20 flex items-center justify-center mr-3">
                        <Mail className="w-4 h-4 text-[--accent]" />
                      </div>
                      <p className="text-sm font-semibold text-[--text-primary]">{t.news.infoTitle}</p>
                    </div>
                    <p className="text-sm text-[--text-secondary] leading-relaxed pl-11">
                      {t.news.infoText}<br />
                      <strong className="text-[--accent]">www.lg-mi.it</strong><br />
                      <strong>e-mail:</strong> <span className="text-[--accent]">comm@lg-mi.it</span>
                    </p>
                  </div>

                  {/* Read More Button */}
                  <motion.a
                    href={featuredArticle.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-3 bg-[--accent] text-white px-8 py-4 rounded-2xl font-bold hover:shadow-xl transition-all group/btn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-base">{t.news.discoverMore}</span>
                    <ArrowUpRight className="w-5 h-5 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                  </motion.a>
                </div>
              </div>


            </div>
          </motion.article>

          {/* Other Articles - Asymmetric Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherArticles.map((article, index) => {
              const colorScheme = accentColors[index % accentColors.length];
              const isWide = index % 5 === 0;

              return (
                <motion.article
                  key={article.id}
                  variants={itemAnimation}
                  className={`group ${isWide ? 'md:col-span-2' : ''}`}
                  whileHover={{ y: -12, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="relative glass rounded-3xl overflow-hidden shadow-xl hover:shadow-luxury-hover transition-all duration-500 border border-transparent hover:border-[--accent]/30 h-full">
                    {/* Glowing effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br from-${colorScheme.from}/5 via-transparent to-${colorScheme.to}/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10`} />

                    {/* Corner accent */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[--accent]/20 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />

                    {/* News Image */}
                    <div className={`relative ${isWide ? 'h-64' : 'h-56'} overflow-hidden`}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-10" />
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/20 to-[--accent]/40 flex items-center justify-center hidden">
                        <Globe className="w-12 h-12 text-[--accent]" />
                      </div>

                      {/* Date Badge with varied position */}
                      <div className={`absolute ${index % 2 === 0 ? 'top-6 left-6' : 'top-6 right-6'} bg-gradient-to-r from-${colorScheme.from} to-${colorScheme.to} text-white px-4 py-2 rounded-2xl text-xs font-bold flex items-center shadow-lg z-20 group-hover:scale-110 transition-transform duration-300`}>
                        <Calendar className="w-3 h-3 mr-2" />
                        {article.date}
                      </div>

                      {/* Overlay effects */}
                      <div className={`absolute inset-0 bg-gradient-to-br from-${colorScheme.from}/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    </div>

                    {/* News Content */}
                    <div className="p-8 relative z-10">
                      <div className="mb-6">
                        <h3 className="text-xl font-display font-bold text-[--text-primary] mb-3 leading-tight group-hover:text-[--accent] transition-colors duration-300">
                          {article.title}
                        </h3>
                        <div className={`w-16 h-1 bg-gradient-to-r from-${colorScheme.from} to-${colorScheme.to} opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:w-24`} />
                      </div>

                      <p className="text-[--text-secondary] text-sm leading-relaxed mb-4 group-hover:text-[--text-primary] transition-colors duration-300 text-justify">
                        {article.description}
                      </p>

                      {article.additionalInfo && (
                        <div className={`bg-${colorScheme.bg} rounded-xl p-4 mb-6 border-l-2 border-${colorScheme.from} group-hover:border-l-4 transition-all duration-300`}>
                          <p className="text-[--text-secondary] text-sm leading-relaxed italic text-justify">
                            {article.additionalInfo}
                          </p>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="bg-[--bg-primary]/50 rounded-2xl p-4 mb-6 group-hover:bg-[--accent]/10 transition-colors duration-300">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 rounded-full bg-[--accent]/20 flex items-center justify-center mr-2">
                            <Mail className="w-3 h-3 text-[--accent]" />
                          </div>
                          <p className="text-xs font-semibold text-[--text-primary]">{t.news.infoTitle}</p>
                        </div>
                        <p className="text-xs text-[--text-secondary] leading-relaxed">
                          {t.news.infoText}<br />
                          <strong className="text-[--accent]">www.lg-mi.it</strong><br />
                          <strong>e-mail:</strong> <span className="text-[--accent]">comm@lg-mi.it</span>
                        </p>
                      </div>

                      {/* Read More Button with varied colors */}
                      <motion.a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-2 bg-gradient-to-r from-${colorScheme.from} to-${colorScheme.to} text-white px-6 py-3 rounded-2xl font-semibold hover:shadow-xl transition-all group/btn w-full justify-center`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span>{t.news.discoverMore}</span>
                        <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </motion.a>
                    </div>

                    {/* Bottom accent line with gradient */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-${colorScheme.from} via-${colorScheme.to} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.div>

        {/* Enhanced Gradient Footer */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[--bg-primary] via-[--bg-primary]/80 to-transparent pointer-events-none" />
      </section>

      <Footer
        onNavigate={onNavigate}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
        onOpenCookiePolicy={onOpenCookiePolicy}
        onOpenTermsOfService={onOpenTermsOfService}
      />

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </>
  );
}
